import React, { useEffect, useState } from 'react';
import { api } from '../../contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const STATUS_LABEL = { self: 'Awaiting self-review', manager: 'Awaiting manager', director: 'Awaiting director', complete: 'Complete' };
const STATUS_COLOR = { self: '#F59E0B', manager: '#3B82F6', director: '#8B5CF6', complete: '#2D6A4F' };

export default function PerfDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/perf/dashboard').then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#6B3FA0]" /></div>;
  if (!data) return null;

  const pie = Object.entries(data.status_counts).map(([k, v]) => ({ name: STATUS_LABEL[k] || k, value: v, key: k }));
  const inProgress = (data.status_counts.self || 0) + (data.status_counts.manager || 0) + (data.status_counts.director || 0);

  return (
    <div className="space-y-4" data-testid="perf-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[['Total reviews', data.total, '#6B3FA0'], ['In progress', inProgress, '#F59E0B'],
          ['Overdue', data.overdue_count || 0, '#C0392B'],
          ['Complete', data.status_counts.complete || 0, '#2D6A4F'], ['Summaries sent', data.summaries_sent, '#01579B']].map(([label, v, c]) => (
          <div key={label} className="rounded-xl border border-[#DED8EB] bg-white p-3" data-testid={`perf-kpi-${label.toLowerCase().replace(/ /g, '-')}`}>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[#9B8FAE]">{label}</p>
            <p className="text-2xl font-black" style={{ color: c, fontFamily: 'Manrope, sans-serif' }}>{v}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-3">
        <div className="lg:col-span-3 rounded-xl border border-[#DED8EB] bg-white p-4" data-testid="perf-category-chart">
          <p className="text-sm font-bold text-[#2D1B4E] mb-2">Average rating by category — self vs manager vs director</p>
          {data.category_averages.length === 0 ? (
            <p className="text-xs text-[#8E85A0] py-10 text-center">No ratings submitted yet — charts appear as reviews come in.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.category_averages} margin={{ left: -20 }}>
                <XAxis dataKey="category" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={50} />
                <YAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="self" name="Self" fill="#F59E0B" radius={[3, 3, 0, 0]} />
                <Bar dataKey="manager" name="Manager" fill="#3B82F6" radius={[3, 3, 0, 0]} />
                <Bar dataKey="director" name="Director" fill="#6B3FA0" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="lg:col-span-2 rounded-xl border border-[#DED8EB] bg-white p-4" data-testid="perf-status-chart">
          <p className="text-sm font-bold text-[#2D1B4E] mb-2">Pipeline status</p>
          {pie.length === 0 ? (
            <p className="text-xs text-[#8E85A0] py-10 text-center">No reviews yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                  {pie.map(p => <Cell key={p.key} fill={STATUS_COLOR[p.key] || '#8E85A0'} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-[#DED8EB] bg-white overflow-hidden" data-testid="perf-employee-table">
        <table className="w-full text-left">
          <thead className="bg-[#FBFAFD] text-[10px] uppercase tracking-wider text-[#9B8FAE]">
            <tr>
              <th className="px-3 py-2">Employee</th><th className="px-3 py-2">Period</th><th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Due</th>
              <th className="px-3 py-2 text-center">Self</th><th className="px-3 py-2 text-center">Manager</th>
              <th className="px-3 py-2 text-center">Director</th><th className="px-3 py-2 text-center">Self↔Mgr gap</th>
              <th className="px-3 py-2 text-center">Summary</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F0ECF7] text-xs">
            {data.employees.map(e => {
              const gapNum = e.self_avg != null && e.manager_avg != null ? e.manager_avg - e.self_avg : null;
              const gap = gapNum != null ? gapNum.toFixed(1) : null;
              return (
                <tr key={e.id} data-testid={`perf-row-${e.id}`}>
                  <td className="px-3 py-2 font-semibold text-[#2D1B4E]">{e.employee_name}</td>
                  <td className="px-3 py-2 text-[#5A4D6E]">{e.period}</td>
                  <td className="px-3 py-2"><span className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: STATUS_COLOR[e.status] || '#8E85A0' }}>{STATUS_LABEL[e.status] || e.status}</span></td>
                  <td className="px-3 py-2">
                    {e.due_date
                      ? <span className={`font-bold ${e.overdue ? 'text-red-700' : 'text-[#B45309]'}`}>{e.overdue ? `⚠ ${e.due_date}` : e.due_date}</span>
                      : <span className="text-[#C7BFD6]">—</span>}
                  </td>
                  <td className="px-3 py-2 text-center font-bold text-[#B45309]">{e.self_avg ?? '—'}</td>
                  <td className="px-3 py-2 text-center font-bold text-[#1D4ED8]">{e.manager_avg ?? '—'}</td>
                  <td className="px-3 py-2 text-center font-bold text-[#6B3FA0]">{e.director_avg ?? '—'}</td>
                  <td className={`px-3 py-2 text-center font-bold ${gapNum != null && Math.abs(gapNum) > 0.7 ? 'text-amber-600' : 'text-[#5A4D6E]'}`}>{gap ?? '—'}</td>
                  <td className="px-3 py-2 text-center">{e.summary_sent ? '✓ sent' : '—'}</td>
                </tr>
              );
            })}
            {data.employees.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-[#8E85A0]">No reviews created yet — start one from the Reviews tab.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
